import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  Alert,
  Box,
  CircularProgress,
  Divider,
} from '@mui/material';
import { setViewAs, type ViewAsState } from '../api/viewAs';

interface ViewAsDialogProps {
  open: boolean;
  onClose: () => void;
  state: ViewAsState;
}

/**
 * Lets a user drop a chosen subset of their own roles to see the app the way someone without them
 * would. The selection is applied server-side, so this is a real reduction in what the API will
 * answer rather than a cosmetic filter over a still-privileged session.
 *
 * Applying reloads the page: role-derived state (navigation, routes, per-page gating) is computed
 * during load across the whole app, so re-reading it in place would leave stale surfaces behind.
 */
const ViewAsDialog: React.FC<ViewAsDialogProps> = ({ open, onClose, state }) => {
  const [selected, setSelected] = React.useState<string[]>(state.suppressedRoles);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Re-seed whenever the dialog is opened so a cancelled edit does not persist into the next open.
  React.useEffect(() => {
    if (open) {
      setSelected(state.suppressedRoles);
      setError(null);
    }
  }, [open, state.suppressedRoles]);

  const toggle = (role: string) => {
    setSelected((cur) => (cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role]));
  };

  const apply = async (roles: string[]) => {
    setSaving(true);
    setError(null);
    try {
      await setViewAs(roles);
      window.location.reload();
    } catch (e) {
      setSaving(false);
      setError(e instanceof Error ? e.message : 'Could not change the view.');
    }
  };

  const kept = state.availableRoles.filter((r) => !selected.includes(r));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>View as</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tick the roles to hide from yourself. They are removed server-side for this browser
          session, so the app responds exactly as it would for someone who does not hold them.
          Nothing here can grant access — it can only take your own away.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button size="small" variant="outlined" disabled={saving} onClick={() => setSelected(state.availableRoles)}>
            Plain user
          </Button>
          <Button size="small" variant="outlined" disabled={saving} onClick={() => setSelected([])}>
            Full access
          </Button>
        </Stack>

        <Divider sx={{ mb: 1 }} />

        <Stack>
          {state.availableRoles.map((role) => (
            <FormControlLabel
              key={role}
              control={
                <Checkbox
                  size="small"
                  checked={selected.includes(role)}
                  onChange={() => toggle(role)}
                  disabled={saving}
                />
              }
              label={<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{role}</Typography>}
            />
          ))}
        </Stack>

        <Alert severity={selected.length > 0 ? 'warning' : 'info'} sx={{ mt: 2 }}>
          {selected.length === 0
            ? 'No roles hidden — you will see the app with your full access.'
            : kept.length === 0
              ? 'Every role hidden — you will see the app as a user with no AskIAM roles at all.'
              : `Keeping ${kept.length} role${kept.length === 1 ? '' : 's'}: ${kept.join(', ')}`}
        </Alert>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={() => void apply(selected)}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Apply and reload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewAsDialog;
